<script setup lang="ts">
import { ref, computed } from 'vue'

const props = defineProps<{
	hasCertificatesConfig: boolean
	hasDegreeConfig: boolean
	configuration: any
	testMode: boolean
}>()
const configuration = ref(props.configuration)
const getBooleanColor = (value: boolean) => (value ? 'primary' : 'danger')

const getBooleanStatus = (value: boolean) => (value ? 'Activo' : 'Inactivo')

// Validaciones para evitar errores cuando las propiedades no existen
const certificatesConfig = computed(() => configuration.value?.certificates || {})
const degreeConfig = computed(() => configuration.value?.degree || {})
</script>
<template>
	<VaCard v-if="props.hasCertificatesConfig" class="config-section" elevation="2">
		<VaCardTitle :class="`section-title ${props.testMode ? 'test-mode' : ''}`">
			<VaIcon name="school" :color="props.testMode ? 'danger' : 'primary'" />
			<span>Configuración de Certificados {{ props.testMode ? '(Modo Prueba)' : '' }}</span>
		</VaCardTitle>
		<VaCardContent>
			<div class="config-grid">
				<!-- Decimales -->
				<VaCard class="config-item" flat>
					<VaCardContent>
						<div class="config-item-header">
							<VaIcon name="numbers" color="info" />
							<h3>Decimales</h3>
						</div>
						<VaChip color="info" size="large">
							{{ certificatesConfig.decimals }}
						</VaChip>
					</VaCardContent>
				</VaCard>

				<!-- Máscara de Ciclo -->
				<VaCard class="config-item" flat>
					<VaCardContent>
						<div class="config-item-header">
							<VaIcon name="schedule" color="warning" />
							<h3>Máscara de Ciclo</h3>
						</div>
						<VaChip color="warning" size="large">
							{{ certificatesConfig.cycleMask }}
						</VaChip>
					</VaCardContent>
				</VaCard>

				<!-- Acentos -->
				<VaCard class="config-item" flat>
					<VaCardContent>
						<div class="config-item-header">
							<VaIcon name="text_fields" color="success" />
							<h3>Quitar Acentos</h3>
						</div>
						<VaChip :color="getBooleanColor(certificatesConfig.hasAccents)" size="large">
							{{ getBooleanStatus(certificatesConfig.hasAccents) }}
						</VaChip>
					</VaCardContent>
				</VaCard>

				<!-- Mayúsculas -->
				<VaCard class="config-item" flat>
					<VaCardContent>
						<div class="config-item-header">
							<VaIcon name="format_size" color="primary" />
							<h3>Convertir a Mayúsculas</h3>
						</div>
						<VaChip :color="getBooleanColor(certificatesConfig.mayus)" size="large">
							{{ getBooleanStatus(certificatesConfig.mayus) }}
						</VaChip>
					</VaCardContent>
				</VaCard>
			</div>
		</VaCardContent>
	</VaCard>

	<!-- Sección de Títulos -->
	<VaCard v-if="props.hasDegreeConfig" class="config-section" elevation="2">
		<VaCardTitle :class="`section-title ${props.testMode ? 'test-mode' : ''}`">
			<VaIcon name="card_membership" :color="props.testMode ? '#FFFFFF' : 'primary'" />
			<span>Configuración de Títulos {{ props.testMode ? '(Modo Prueba)' : '' }}</span>
		</VaCardTitle>
		<VaCardContent>
			<div v-if="!props.testMode" class="config-grid">
				<!-- Conector Carrera Nivel -->
				<VaCard class="config-item" flat>
					<VaCardContent>
						<div class="config-item-header">
							<VaIcon name="link" color="info" />
							<h3>Conector Carrera-Nivel</h3>
						</div>
						<VaChip color="info" size="large">
							{{ degreeConfig.conectorCarreraNivel }}
						</VaChip>
						<span class="text-xs text-gray-500 font-medium ml-2">Ejemplo: Ingeniería {{ degreeConfig.conectorCarreraNivel }} ......</span>
					</VaCardContent>
				</VaCard>

				<!-- Acentos -->
				<VaCard class="config-item" flat>
					<VaCardContent>
						<div class="config-item-header">
							<VaIcon name="text_fields" color="success" />
							<h3>Quitar Acentos</h3>
						</div>
						<VaChip :color="getBooleanColor(degreeConfig.hasAccents)" size="large">
							{{ getBooleanStatus(degreeConfig.hasAccents) }}
						</VaChip>
					</VaCardContent>
				</VaCard>
			</div>

			<!-- Servicios -->
			<div v-if="degreeConfig.services" class="services-section">
				<h3 class="services-title">
					<VaIcon name="cloud" :color="props.testMode ? 'danger' : 'primary'" />
					Servicios Configurados {{ props.testMode ? '(Modo Prueba)' : '' }}
				</h3>
				<div class="services-grid">
					<VaCard class="service-item" flat>
						<VaCardContent>
							<div class="service-header">
								<VaIcon name="dns" color="primary" />
								<h4>Servicio</h4>
							</div>
							<div class="service-details">
								<div class="service-field">
									<strong>ID:</strong>
									<VaChip color="info" size="small">
										{{ degreeConfig.services.id }}
									</VaChip>
								</div>
								<div class="service-field">
									<strong>URL:</strong>
									<VaChip color="warning" size="small">
										{{ degreeConfig.services.url }}
									</VaChip>
								</div>
								<div class="service-field">
									<strong>Usuario:</strong>
									<VaChip color="success" size="small">
										{{ degreeConfig.services.user }}
									</VaChip>
								</div>
							</div>
						</VaCardContent>
					</VaCard>
				</div>
			</div>
		</VaCardContent>
	</VaCard>
</template>
<style scoped>
.config-section {
	margin-bottom: 2rem;
	border-radius: 12px;
	overflow: hidden;
}

.section-title {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	font-size: 1.4rem;
	font-weight: 600;
	color: #2c3e50;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	padding: 1.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.config-grid {
		grid-template-columns: 1fr;
		gap: 1rem;
	}

	.services-grid {
		grid-template-columns: 1fr;
	}

	.section-title {
		font-size: 1.2rem;
		padding: 1rem;
	}
}
.section-title.test-mode {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	color: #ffffff;
}

.config-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 1.5rem;
	padding: 1rem 0;
}

.config-item {
	border: 1px solid #e9ecef;
	border-radius: 8px;
	transition: all 0.3s ease;
}

.config-item:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.config-item-header {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 1rem;
	color: #062342;
}

.config-item-header h3 {
	margin: 0;
	font-size: 1.1rem;
	font-weight: 600;
	color: #495057;
}

.services-section {
	margin-top: 2rem;
	padding-top: 2rem;
	border-top: 2px solid #e9ecef;
}

.services-title {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	font-size: 1.3rem;
	font-weight: 600;
	color: #2c3e50;
	margin-bottom: 1.5rem;
}

.services-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
	gap: 1rem;
}

.service-item {
	border: 1px solid #e9ecef;
	border-radius: 8px;
	transition: all 0.3s ease;
}

.service-item:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.service-header {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 1rem;
}

.service-header h4 {
	margin: 0;
	font-size: 1rem;
	font-weight: 600;
	color: #495057;
}

.service-details {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.service-field {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
}

.service-field strong {
	color: #6c757d;
	font-size: 0.9rem;
	min-width: 60px;
}
</style>
